'use client';

import styled from '@emotion/styled';
import { Icon, Text, formatPrice } from '@template-project/afrodita-components';
import type { CartItemEntity } from '@/domain/entities/cart.entity';
import { useTranslation } from '@/lib/i18n/client';
import { useIntl } from '@/lib/intl';
import { usePackName } from '@/lib/hooks/use-pack-name';

const GroupWrapper = styled.div`
  padding: ${({ theme }) => theme.space.sm} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.color};
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${({ theme }) => theme.space.sm};
  margin-bottom: ${({ theme }) => theme.space.sm};
`;

const PackTitle = styled(Text)`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  flex: 1;
  min-width: 0;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  padding: ${({ theme }) => theme.space.xs};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.secondary};
  flex-shrink: 0;

  &:hover {
    color: ${({ theme }) => theme.colors.semantic.urgent.color};
  }
`;

const ComponentRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.sm};
  padding: ${({ theme }) => theme.space.xs} 0 ${({ theme }) => theme.space.xs}
    ${({ theme }) => theme.space.sm};
  border-left: 2px solid ${({ theme }) => theme.colors.border.color};
  margin-left: ${({ theme }) => theme.space.xs};
`;

const ComponentImage = styled.img`
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.radius.small};
  flex-shrink: 0;
`;

const ImagePlaceholder = styled.div`
  width: 56px;
  height: 56px;
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.radius.small};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.shades[200].color};
`;

const ComponentDetails = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ComponentTitle = styled(Text)`
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
`;

const VariantLine = styled(Text)`
  font-size: ${({ theme }) => theme.fontSizes[0]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const FooterRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: ${({ theme }) => theme.space.md};
  margin-top: ${({ theme }) => theme.space.sm};
`;

const QtyPickerWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
`;

const QtyButton = styled.button`
  width: ${({ theme }) => theme.space['2xl']};
  height: ${({ theme }) => theme.space['2xl']};
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  background: ${({ theme }) => theme.colors.background.primary};
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radius.small};
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const QtyDisplay = styled.span`
  min-width: ${({ theme }) => theme.space['2xl']};
  text-align: center;
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
`;

const PricingBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const FinalPrice = styled(Text)`
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
`;

const ComparePrice = styled(Text)`
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: line-through;
`;

const PackBadge = styled.span`
  display: inline-block;
  padding: 3px 8px;
  background: ${({ theme }) => theme.colors.semantic.positive.light?.color ?? '#dfefeb'};
  color: ${({ theme }) => theme.colors.semantic.positive.color};
  border-radius: ${({ theme }) => theme.radius.small};
  font-size: ${({ theme }) => theme.fontSizes[0]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

function formatAttributeLabel(
  t: ReturnType<typeof useTranslation>['t'],
  name: string,
  value: string,
): string {
  const lower = name.toLowerCase();
  if (lower === 'color' || lower === 'colour') {
    return t('cart.color', { color: value, defaultValue: 'Color: {{color}}' });
  }
  if (lower === 'size') {
    return t('cart.size', { size: value, defaultValue: 'Size: {{size}}' });
  }
  return `${name}: ${value}`;
}

export interface PackCartGroupProps {
  packId: string;
  items: CartItemEntity[];
  isLoading?: boolean;
  onPackQuantityChange: (packId: string, quantity: number) => void;
  onPackRemove: (packId: string) => void;
}

export function PackCartGroup({
  packId,
  items,
  isLoading = false,
  onPackQuantityChange,
  onPackRemove,
}: PackCartGroupProps) {
  const { t } = useTranslation('sidecart');
  const { countryConfig } = useIntl();
  const { data: packName } = usePackName(packId);

  const priceOptions = {
    currency: countryConfig.currency.code,
    locale: countryConfig.locale,
    symbolOverride: countryConfig.currency.format,
  };

  const packLabel =
    packName ||
    t('cart.packFallback', { id: packId.slice(0, 8), defaultValue: 'Pack {{id}}' });

  const quantity = items[0]?.quantity ?? 1;

  const lineTotalCents = items.reduce((sum, item) => sum + item.price.display * item.quantity, 0);
  const compareTotalCents = items.reduce((sum, item) => {
    const unit = item.compare_at_price?.display ?? item.price.display;
    return sum + unit * item.quantity;
  }, 0);

  const showCompare = compareTotalCents > lineTotalCents;

  return (
    <GroupWrapper data-loading={isLoading}>
      <HeaderRow>
        <PackTitle variant="body" weight="bold">
          {packLabel}
        </PackTitle>
        <RemoveButton
          type="button"
          aria-label={t('cart.removePack', { defaultValue: 'Remove pack' })}
          onClick={() => onPackRemove(packId)}
        >
          <Icon.PDP.Trash width={18} height={18} />
        </RemoveButton>
      </HeaderRow>

      {items.map((item) => (
        <ComponentRow key={item.id}>
          {item.image_url ? (
            <ComponentImage src={item.image_url} alt={item.name} />
          ) : (
            <ImagePlaceholder>
              <Icon.PDP.ShoppingBag width={24} height={24} />
            </ImagePlaceholder>
          )}
          <ComponentDetails>
            <ComponentTitle variant="body">{item.name}</ComponentTitle>
            {(item.variation_attributes ?? []).map((attr) => (
              <VariantLine key={`${item.id}-${attr.name}`} variant="body">
                {formatAttributeLabel(t, attr.name, attr.value)}
              </VariantLine>
            ))}
          </ComponentDetails>
        </ComponentRow>
      ))}

      <FooterRow>
        <QtyPickerWrapper>
          <QtyButton
            type="button"
            aria-label={t('cart.decreaseQuantity', { defaultValue: 'Decrease quantity' })}
            disabled={isLoading || quantity <= 1}
            onClick={() => onPackQuantityChange(packId, quantity - 1)}
          >
            −
          </QtyButton>
          <QtyDisplay>{quantity}</QtyDisplay>
          <QtyButton
            type="button"
            aria-label={t('cart.increaseQuantity', { defaultValue: 'Increase quantity' })}
            disabled={isLoading}
            onClick={() => onPackQuantityChange(packId, quantity + 1)}
          >
            +
          </QtyButton>
        </QtyPickerWrapper>

        <PricingBlock>
          <FinalPrice variant="body">
            {formatPrice(lineTotalCents, priceOptions)}
          </FinalPrice>
          {showCompare && (
            <ComparePrice variant="body">
              {formatPrice(compareTotalCents, priceOptions)}
            </ComparePrice>
          )}
          <PackBadge>{t('cart.packApplied', { defaultValue: 'Bundle kit applied' })}</PackBadge>
        </PricingBlock>
      </FooterRow>
    </GroupWrapper>
  );
}
